/**
 * Runtime configuration — loaded from /config.json before the app renders, so
 * one build serves any deployment (the cms-front posture).
 */

export interface RuntimeConfig {
  /** skills API origin (…/v1). */
  skillsApiUrl: string;
  /** Auth service origin (OTP logins, refresh, logout). */
  authApiUrl: string;
  /** LOCAL ONLY: scripts/dev/jwks-dev.mjs origin — enables the test sign-in buttons. */
  devTokenUrl?: string;
  environment?: string;
}

let current: RuntimeConfig | null = null;

const invalid = (reason: string) => new Error(`config.json is invalid: ${reason}`);
const isOrigin = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//.test(v);

export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  if (typeof raw !== 'object' || raw === null) throw invalid('not an object');
  const o = raw as Record<string, unknown>;
  if (!isOrigin(o.skillsApiUrl)) throw invalid('skillsApiUrl must be an http(s) origin');
  if (!isOrigin(o.authApiUrl)) throw invalid('authApiUrl must be an http(s) origin');
  return {
    skillsApiUrl: o.skillsApiUrl.replace(/\/+$/, ''),
    authApiUrl: o.authApiUrl.replace(/\/+$/, ''),
    devTokenUrl: isOrigin(o.devTokenUrl) ? o.devTokenUrl.replace(/\/+$/, '') : undefined,
    environment: typeof o.environment === 'string' ? o.environment : undefined,
  };
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 12_000);
  try {
    const res = await fetch('/config.json', { cache: 'no-cache', signal: ctrl.signal });
    if (!res.ok) throw invalid(`HTTP ${res.status}`);
    current = parseRuntimeConfig(await res.json());
    return current;
  } finally {
    clearTimeout(timer);
  }
}

export function runtimeConfig(): RuntimeConfig {
  if (!current) throw new Error('runtime config not loaded');
  return current;
}

export function setRuntimeConfigForTests(cfg: RuntimeConfig): void {
  current = cfg;
}
